import { DropdownFamily } from '$tablet-app/src/app/components/DropdownFamily/DropdownFamily';
import {
  FloatingActions,
  useFloatingActionControl,
} from '$tablet-app/src/app/components/FloatingActions';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { KeyboardAvoid } from '$tablet-app/src/app/components/KeyboardAvoid';
import { TaskListType, User } from '@akin-home/amplify';
import { Button } from '@akin-home/buttons/native';
import { ChecklistModel } from '@akin-home/checklists';
import { TextInput } from '@akin-home/forms/native';
import React, { FC, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import styled from 'styled-components/native';
import ChecklistScreenHeader from './components/ChecklistScreenHeader';
import ChecklistsScreenViewBody from './components/ChecklistsScreenViewBody';

interface Props {
  checklists?: ChecklistModel[];

  searchInput?: string;

  users?: User[];

  filteredUserIDs?: string[];

  isLoading?: boolean;

  onPressNewChecklist?: (listType?: TaskListType) => void;

  onChangeUserFilter?: (users: User[]) => void;

  onChangeSearchInput?: (searchInput: string) => void;
}

const defaults = {
  checklists: [],
  filteredUserIDs: [],
  searchInput: '',
  users: [],
};

const ChecklistsScreenView: FC<Props> = (props) => {
  const {
    checklists = defaults.checklists,
    filteredUserIDs = defaults.filteredUserIDs,
    searchInput = defaults.searchInput,
    users = defaults.users,
    isLoading,
    onPressNewChecklist,
    onChangeUserFilter,
    onChangeSearchInput,
  } = props;

  const { scrollHandler, floating_action_styles } = useFloatingActionControl();

  /** Get the selected users from the list of ids */
  const selectedUsers = useMemo(
    () => users.filter((u) => filteredUserIDs.includes(u.id as string)),
    [users, filteredUserIDs]
  );

  const onNewPress = (type?: TaskListType) => () => onPressNewChecklist?.(type);

  return (
    <Screen>
      <ChecklistScreenHeader>
        <HeaderFilters>
          <TextInput
            outerContainerStyle={styles.headerContentContainer}
            preset="white"
            value={searchInput}
            placeholder="Search lists"
            icon="search"
            onChangeText={onChangeSearchInput}
          />

          <View style={styles.headerContentContainer}>
            <DropdownFamily
              title="People"
              onChange={onChangeUserFilter}
              value={selectedUsers}
              familyMembers={users}
              multiSelect
            />
          </View>
        </HeaderFilters>
      </ChecklistScreenHeader>

      {isLoading ? (
        <FullScreenLoader />
      ) : (
        <KeyboardAvoid style={styles.keyboardAvoid}>
          <Body
            onScroll={scrollHandler}
            scrollEventThrottle={16}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={styles.scroller}
          >
            <ChecklistsScreenViewBody
              checklists={checklists}
              onPressNewChecklist={onPressNewChecklist}
            />
          </Body>
        </KeyboardAvoid>
      )}

      <FloatingActions style={floating_action_styles}>
        <Button icon="plus" onPress={onNewPress()} size="large">
          New list
        </Button>
      </FloatingActions>
    </Screen>
  );
};

const Screen = styled.View`
  flex: 1;
`;

const Body = styled(Animated.ScrollView)`
  padding-bottom: 100px;
`;

const HeaderFilters = styled.View`
  width: 100%;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  column-gap: 20px;
  row-gap: 10px;
`;

const styles = StyleSheet.create({
  keyboardAvoid: {
    flex: 1,
  },
  headerContentContainer: {
    minWidth: 250,
    flexGrow: 1,
  },
  scroller: {
    gap: 20,
    flexDirection: 'row',
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 100,
  },
});

export default ChecklistsScreenView;
